import type { OnpageIssueView } from './url-detail-onpage-issues';

/**
 * Which crawler checks the review surface may present as work, read against the page's own stored
 * crawl.
 *
 * The crawl runs with JavaScript on, so DataForSEO judges the page *after* its scripts have run: a
 * chat widget's iframe, a personalisation widget's 97 KB stylesheet, the extra tags a script puts in
 * `<head>`. Three checks built on that rendered document were sent to writers as template fixes, and
 * on the 25 graded ManyRequests pages every one of them was wrong or nearly so (TECHNICAL_DEBT §0bx):
 * the served HTML holds no `<iframe>`, its text is 19–44% of the HTML, and its biggest element is the
 * article body. Each rule below reads what the crawl already stored and drops the check when that
 * evidence contradicts it. Nothing is fetched.
 *
 * Only the review surface (the belt, and the export and MCP that read it) drops anything. URL
 * Detail still lists every check the crawler raised: it is the audit view, the same split
 * `site-wide-suppression` makes.
 */

/** The bar DataForSEO's `low_content_rate` check measures text against: 10% of the document. */
export const LOW_CONTENT_RATE_BAR = 0.1;

/**
 * How close to `LOW_CONTENT_RATE_BAR` our measure may sit before the check is too close to call: 1.1
 * points, so it is raised only under 8.9%.
 *
 * Our measure is the crawl's text size over its stored document without inline styles; a reader
 * gets the served page. Against the served page's visible-text share (2026-10-21, 50 pages:
 * SlideSpeak's 20 queue pages, ManyRequests' 29 plus the case-study page graded wrong at 11.0% in
 * sprint 5), ours reads lower on every page, by 0.6–1.7 points on SlideSpeak and 0.0–5.7 on
 * ManyRequests, the median 1.1 on each site and on both. The case study measured 10.0% here and
 * 11.0% served. SlideSpeak's flagged pages run 0.6–3.8% here, far under the margin.
 */
export const LOW_CONTENT_RATE_MARGIN = 0.011;

/**
 * "Has more that 1500 nodes.", as `resource:` codes normalise it. The limit is read from the title.
 */
export const DOM_SIZE_CHECK = /has more that # nodes/;

/**
 * How many elements over the DOM-size limit the crawl's copy must count before the check is raised:
 * 32.
 *
 * The crawler counts the page after its scripts have run, and they add elements the page as served
 * does not have: analytics and ad tags, a chat button, tracking pixels. Counted the way a browser
 * parses it, the stored copy ran above the served HTML on all 49 queue pages measured (2026-10-21):
 * SlideSpeak 24–271 (median 29; 28–29 on every free-tool page), ManyRequests 31–511 (median 56),
 * both sites together a median of 32. Three SlideSpeak tool pages flagged at 1,503, 1,515 and 1,528
 * elements are 1,474, 1,486 and 1,499 as served, under the limit, and were graded wrong; the next
 * one up, 1,542 (1,513 served), was graded right and stays.
 */
export const DOM_SIZE_SCRIPT_MARGIN = 32;

/**
 * "Has node with more than 60 childs.", as `resource:` codes normalise it (digits to `#`).
 *
 * DataForSEO reports the first element in the rendered document with more than 60 direct children,
 * at a line and column. Re-measured on the stored HTML of both sites (2026-10-15): on all 33
 * ManyRequests queue pages that element is `<head>` (61–68 children once scripts have added theirs;
 * the served `<head>` has 40), and on SlideSpeak's 25 it is `<head>` on 22 and `<body>` on 3. Never an
 * element a writer edits. The article bodies the grader found over 60 (75–452 flat paragraphs) are
 * a list of paragraphs, which costs nothing; 14 of the 16 were graded wrong as advice.
 */
export const NODE_CHILDREN_CHECK = /has node with more than # childs/;

/**
 * "The closing tag and the currently open tag do not match.", normalised.
 *
 * On SlideSpeak's 25 baseline pages the crawler raised it 1,033 times (39–62 a page). Not one is
 * real: every end tag in those documents closes the element that is open (34,157 end tags in the
 * stored HTML, 28,380 in the served snapshots, checked with a stack parse that does catch a planted
 * mismatch). 527 of the reported positions are inline-SVG `</radialGradient>` and `</linearGradient>`
 * tags, which HTML keeps in mixed case and the crawler's validator compares as if it did not; the
 * rest do not land on a tag at all. And where a real one exists, the browser repairs it: it is not
 * something a writer fixes.
 */
export const CLOSING_TAG_MISMATCH_CHECK = /closing tag and the currently open tag do not match/;

/** A check the review surface does not show, and why. The reason is client-safe text. */
export interface WithheldTechnicalCheck {
  code: string;
  title: string;
  reason: string;
}

export interface ReviewTechnicalChecks {
  shown: OnpageIssueView[];
  withheld: WithheldTechnicalCheck[];
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function rawData(analysis: unknown): Record<string, unknown> | null {
  return record(record(analysis)?.['rawData']);
}

/** `<frame>` or `<frameset>`, never `<iframe>`: the `<` sits directly before "frame". */
const FRAME_ELEMENT = /<frame(?:set)?[\s>/]/i;

const framesScan = new WeakMap<object, boolean | null>();

/**
 * Whether the stored rendered HTML holds a real frame, or null when no HTML was stored.
 *
 * Cached per analysis object: the belt calls this on every change-detection pass, and the stored
 * document is 170–640 KB.
 */
function storedHtmlHasFrames(analysis: unknown): boolean | null {
  const key = record(analysis);
  if (!key) return null;
  if (framesScan.has(key)) return framesScan.get(key)!;
  const html = rawData(analysis)?.['rawHtml'];
  const verdict = typeof html === 'string' && html.length > 0 ? FRAME_ELEMENT.test(html) : null;
  framesScan.set(key, verdict);
  return verdict;
}

/** `meta.content.plain_text_size` of the stored crawl, or null. */
function storedTextBytes(analysis: unknown): number | null {
  const meta = record(record(rawData(analysis)?.['pageData'])?.['meta']);
  const size = Number(record(meta?.['content'])?.['plain_text_size']);
  return Number.isFinite(size) ? size : null;
}

const INLINE_STYLE = /<style\b[^>]*>[\s\S]*?<\/style>/gi;

const styleScan = new WeakMap<object, { length: number; styleChars: number } | null>();

/** The stored HTML's length and how much of it is inline `<style>`, or null when none was stored. */
function storedHtmlStyleShare(analysis: unknown): { length: number; styleChars: number } | null {
  const key = record(analysis);
  if (!key) return null;
  if (styleScan.has(key)) return styleScan.get(key)!;
  const html = rawData(analysis)?.['rawHtml'];
  let result: { length: number; styleChars: number } | null = null;
  if (typeof html === 'string' && html.length > 0) {
    let styleChars = 0;
    for (const m of html.match(INLINE_STYLE) ?? []) styleChars += m.length;
    result = { length: html.length, styleChars };
  }
  styleScan.set(key, result);
  return result;
}

/**
 * The page's text as a share of its stored HTML once inline stylesheets are left out, or null when
 * the crawl stored too little to say.
 *
 * Why styles: the crawler measures the document after its scripts have run, and what scripts add is
 * mostly stylesheets. On ManyRequests the stored document carries ~113 KB of inline `<style>` against
 * 7.5 KB in the served HTML (a personalisation widget's base styles, TECHNICAL_DEBT §0m). Leaving them
 * out lands within 1.6 points of the served HTML's own rate on all 22 flagged ManyRequests pages and
 * within 0.7 on all 25 SlideSpeak pages, on the same side of the bar every time: 18.0–26.9% on
 * ManyRequests, where the grader found the served page well over 10% and the advice wrong, and
 * 0.8–4.6% on SlideSpeak, where the served page really is under 10% (an inline Next.js script
 * payload) and the advice was graded right.
 */
function textRateWithoutStyles(analysis: unknown): number | null {
  const text = storedTextBytes(analysis);
  const html = storedHtmlStyleShare(analysis);
  if (text == null || !html) return null;
  const rest = html.length - html.styleChars;
  return rest > 0 ? text / rest : null;
}

/**
 * A link or resource the page points at over plain http: an `href`, `src` or form `action` that
 * starts `http://`. Namespace URIs (`xmlns="http://www.w3.org/2000/svg"`) are not links and do not
 * match.
 */
const HTTP_LINK = /\s(?:href|src|action)\s*=\s*["']?\s*http:\/\//i;

const httpScan = new WeakMap<object, boolean | null>();

/** Whether the stored HTML points anywhere over plain http, or null when none was stored. */
function storedHtmlHasHttpLink(analysis: unknown): boolean | null {
  const key = record(analysis);
  if (!key) return null;
  if (httpScan.has(key)) return httpScan.get(key)!;
  const html = rawData(analysis)?.['rawHtml'];
  const verdict = typeof html === 'string' && html.length > 0 ? HTTP_LINK.test(html) : null;
  httpScan.set(key, verdict);
  return verdict;
}

const JSON_LD = /<script\b[^>]*\btype\s*=\s*["']?application\/ld\+json["']?[^>]*>\s*[^<\s]/gi;

/** A comment, a doctype or CDATA section, an end tag, or a start tag with its attributes. */
const MARKUP_TOKEN = /<!--[\s\S]*?-->|<![^>]*>|<\/([a-zA-Z][\w:-]*)[^>]*>|<([a-zA-Z][\w:-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;

const ATTRIBUTE = /([^\s"'>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;

/** Elements with no end tag: they never hold anything. */
const VOID_ELEMENTS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'
]);

/**
 * Elements whose content a browser reads as text, not markup. `noscript` is one when scripts run,
 * which is how a reader's browser (and the grader's count) reads the page.
 */
const RAW_TEXT_ELEMENTS = new Set(['script', 'style', 'textarea', 'title', 'noscript', 'iframe', 'xmp', 'noembed', 'noframes']);

/** The site's own frame around the page: navigation, header, footer, sidebars. */
const SITE_CHROME_ELEMENTS = new Set(['nav', 'header', 'footer', 'aside']);
const SITE_CHROME_ROLES = new Set(['navigation', 'banner', 'contentinfo', 'complementary']);

const rawTextEnds = new Map<string, RegExp>();

/** The end tag that closes a raw-text element, found case-insensitively from `lastIndex`. */
function rawTextEnd(name: string): RegExp {
  let re = rawTextEnds.get(name);
  if (!re) {
    re = new RegExp(`</${name}[^>]*>`, 'gi');
    rawTextEnds.set(name, re);
  }
  return re;
}

function parseAttributes(source: string): Map<string, string> {
  const out = new Map<string, string>();
  ATTRIBUTE.lastIndex = 0;
  for (let m = ATTRIBUTE.exec(source); m; m = ATTRIBUTE.exec(source)) {
    const name = m[1].toLowerCase();
    if (!out.has(name)) out.set(name, m[2] ?? m[3] ?? m[4] ?? '');
  }
  return out;
}

/** Not rendered, so nothing inside it is read: `hidden`, `aria-hidden="true"`, or hidden by its style. */
function hidesItsContent(attrs: Map<string, string>): boolean {
  return (
    attrs.has('hidden') ||
    (attrs.get('aria-hidden') ?? '').trim().toLowerCase() === 'true' ||
    /display\s*:\s*none|visibility\s*:\s*hidden/i.test(attrs.get('style') ?? '')
  );
}

/** A vector graphic: icons, logos drawn as shapes, gradients. `.svg` in the source, or in an optimiser URL. */
const VECTOR_SOURCE = /\.svg(?:$|[?#&%"'\s])/i;

/** Laid over its whole container as an absolute layer, the way a background is. */
function isBackgroundLayer(style: string): boolean {
  return (
    /position\s*:\s*absolute/i.test(style) &&
    /(?:^|[;\s])width\s*:\s*100%/i.test(style) &&
    /(?:^|[;\s])height\s*:\s*100%/i.test(style)
  );
}

/**
 * Whether an image is one a reader would need described, or null when it has alt text or needs none.
 * An image with an empty alt inside a link is settled later, by the link (`scanStoredMarkup`).
 *
 * WCAG asks nothing of a hidden image (`aria-hidden`, `hidden`, hidden by style, inside an element
 * that is), of a tracking pixel, or of one marked decorative with `role="presentation"`. And `alt=""`
 * is how HTML marks an image decorative, which is right for the site's navigation icons, vector
 * graphics and background layers. It is not right for a screenshot, a photo or a preview in the
 * page itself, which is where an empty alt still leaves a reader with nothing (TECHNICAL_DEBT §0bx).
 *
 * Measured 2026-10-21 on the 49 queue pages of both sites, served and stored copies alike: every
 * SlideSpeak free-tool page and its two flagged posts come out with nothing missing (feature
 * thumbnails inside an `aria-hidden` wrapper, 16 px icons, gradient blobs: the S4 grader's
 * "decorative"), one post keeps its 19 tutorial screenshots, and every ManyRequests page keeps its
 * hero, preview, author and article images (graded right), while the 14 navigation icons on each are
 * set aside.
 */
function imageNeedsAlt(attrs: Map<string, string>, insideHidden: boolean, insideChrome: boolean): boolean {
  const alt = attrs.get('alt');
  if (alt != null && alt.trim() !== '') return false;
  if (insideHidden || hidesItsContent(attrs)) return false;
  if (/^(presentation|none)$/i.test((attrs.get('role') ?? '').trim())) return false;
  const pixel = (name: string) => {
    const value = (attrs.get(name) ?? '').trim();
    return value !== '' && Number(value) <= 1;
  };
  if (pixel('width') || pixel('height')) return false;
  if (alt == null) return true;
  const source = `${attrs.get('src') ?? ''} ${attrs.get('srcset') ?? ''}`;
  return !(insideChrome || VECTOR_SOURCE.test(source) || isBackgroundLayer(attrs.get('style') ?? ''));
}

interface StoredMarkupFacts {
  jsonLdBlocks: number;
  /** Elements, counted the way a browser parses the document. */
  elements: number;
  images: number;
  imagesNeedingAlt: number;
  /** Of those, how many sit in the site's navigation, header, footer or sidebar. */
  imagesNeedingAltInChrome: number;
}

/** An element still open during the scan, with what its descendants inherit. */
interface OpenElement {
  name: string;
  hidden: boolean;
  chrome: boolean;
  /**
   * Set on a link (`<a href>`). An image with `alt=""` inside a link that names itself with its
   * own text (or `aria-label`, `title`) is the link's picture, not content: the text already says
   * where it goes, and WCAG asks for the empty alt so a screen reader does not say it twice.
   * ManyRequests' template and resource pages end on three related-template cards, each a
   * thumbnail with `alt=""` beside the template's name in an `<h3>`, inside one `<a>`. Those
   * cards were the whole of "images missing alt text" on 8 of its 25 `/review` pages
   * (2026-11-03). An image that is the link's only content still needs alt, and so does an
   * image with no alt attribute at all, which is an omission rather than a choice.
   */
  link?: { named: boolean; emptyAltImages: number; emptyAltImagesInChrome: number };
}

/**
 * One pass over the stored HTML: elements counted, images read with what they sit inside.
 *
 * A tag scan, not a DOM: this runs in the browser and on the server alike. End tags close back to
 * the nearest open element of the same name, raw-text elements are skipped whole. On the 49 queue
 * pages of both sites the count matches a full HTML parser exactly on every stored copy, and on 48
 * of the 49 served copies (the other is 5 over).
 */
function scanStoredMarkup(html: string): StoredMarkupFacts {
  const open: OpenElement[] = [];
  let elements = 0;
  let images = 0;
  let imagesNeedingAlt = 0;
  let imagesNeedingAltInChrome = 0;
  /** A link closes: its empty-alt images need describing only if nothing else names it. */
  const settle = (closed: OpenElement[]) => {
    for (const el of closed) {
      if (!el.link || el.link.named) continue;
      imagesNeedingAlt += el.link.emptyAltImages;
      imagesNeedingAltInChrome += el.link.emptyAltImagesInChrome;
    }
  };
  let textFrom = 0;
  MARKUP_TOKEN.lastIndex = 0;
  for (let m = MARKUP_TOKEN.exec(html); m; m = MARKUP_TOKEN.exec(html)) {
    if (m.index > textFrom && /\S/.test(html.slice(textFrom, m.index))) {
      for (const el of open) if (el.link) el.link.named = true;
    }
    textFrom = MARKUP_TOKEN.lastIndex;
    if (m[1]) {
      const name = m[1].toLowerCase();
      for (let i = open.length - 1; i >= 0; i--) {
        if (open[i].name === name) {
          settle(open.splice(i));
          break;
        }
      }
      continue;
    }
    if (!m[2]) continue;
    const name = m[2].toLowerCase();
    elements += 1;
    const source = m[3] ?? '';
    const parent = open[open.length - 1];
    if (name === 'img') {
      images += 1;
      const attrs = parseAttributes(source);
      if (imageNeedsAlt(attrs, parent?.hidden ?? false, parent?.chrome ?? false)) {
        const link = [...open].reverse().find((el) => el.link)?.link;
        // `imageNeedsAlt` passed it, so an alt attribute here is an empty one.
        if (link && attrs.has('alt')) {
          link.emptyAltImages += 1;
          if (parent?.chrome) link.emptyAltImagesInChrome += 1;
        } else {
          imagesNeedingAlt += 1;
          if (parent?.chrome) imagesNeedingAltInChrome += 1;
        }
      }
      continue;
    }
    if (RAW_TEXT_ELEMENTS.has(name)) {
      const end = rawTextEnd(name);
      end.lastIndex = MARKUP_TOKEN.lastIndex;
      if (!end.exec(html)) break;
      MARKUP_TOKEN.lastIndex = end.lastIndex;
      textFrom = end.lastIndex;
      continue;
    }
    if (VOID_ELEMENTS.has(name) || /\/\s*$/.test(source)) continue;
    const attrs = parseAttributes(source);
    open.push({
      name,
      hidden: (parent?.hidden ?? false) || hidesItsContent(attrs),
      chrome:
        (parent?.chrome ?? false) ||
        SITE_CHROME_ELEMENTS.has(name) ||
        SITE_CHROME_ROLES.has((attrs.get('role') ?? '').trim().toLowerCase()),
      link:
        name === 'a' && attrs.has('href')
          ? {
              named: (attrs.get('aria-label') || attrs.get('title') || '').trim() !== '',
              emptyAltImages: 0,
              emptyAltImagesInChrome: 0
            }
          : undefined
    });
  }
  settle(open);
  return {
    jsonLdBlocks: (html.match(JSON_LD) ?? []).length,
    elements,
    images,
    imagesNeedingAlt,
    imagesNeedingAltInChrome
  };
}

const markupScan = new WeakMap<object, StoredMarkupFacts | null>();

/** What the stored HTML holds, or null when none was stored. Cached per analysis. */
function storedMarkupFacts(analysis: unknown): StoredMarkupFacts | null {
  const key = record(analysis);
  if (!key) return null;
  if (markupScan.has(key)) return markupScan.get(key)!;
  const html = rawData(analysis)?.['rawHtml'];
  const facts = typeof html === 'string' && html.length > 0 ? scanStoredMarkup(html) : null;
  markupScan.set(key, facts);
  return facts;
}

/** Why one check is not shown on the review surface, or null to show it. */
function withheldReason(issue: OnpageIssueView, analysis: unknown): string | null {
  if (issue.code === 'frame') {
    // An iframe is not a defect: the framed document is indexed on its own, and the page's own
    // content is untouched. Frames (`<frameset>`) are the case the check was written for.
    return storedHtmlHasFrames(analysis) === false
      ? 'The page has no frames. Its iframes are embeds or widgets a script adds (chat, reCAPTCHA, video), which are not something to fix.'
      : null;
  }
  if (issue.code === 'low_content_rate') {
    const rate = textRateWithoutStyles(analysis);
    if (rate == null) return null;
    if (rate >= LOW_CONTENT_RATE_BAR) {
      return `Leaving out the page's stylesheets, text is ${Math.round(rate * 100)}% of the page, over the 10% bar. The low figure comes from styles added when the page runs, not from thin copy.`;
    }
    return rate >= LOW_CONTENT_RATE_BAR - LOW_CONTENT_RATE_MARGIN
      ? `Leaving out the page's stylesheets, text is ${(rate * 100).toFixed(1)}% of the page, within ${(LOW_CONTENT_RATE_MARGIN * 100).toFixed(1)} points of the 10% bar. The page as served usually reads higher than the crawl's copy, so it is too close to call.`
      : null;
  }
  if (DOM_SIZE_CHECK.test(issue.code)) {
    const limit = Number(/\d+/.exec(issue.title)?.[0]);
    const facts = storedMarkupFacts(analysis);
    if (!facts || !Number.isFinite(limit) || facts.elements - DOM_SIZE_SCRIPT_MARGIN > limit) return null;
    const count = (n: number) => n.toLocaleString('en-US');
    return `With its scripts run, the page has ${count(facts.elements)} elements. Scripts such as analytics tags and chat widgets add about ${DOM_SIZE_SCRIPT_MARGIN} that the page as served does not have, so it is too close to the ${count(limit)} limit to call.`;
  }
  if (issue.code === 'has_micromarkup') {
    // The crawler's check looks for microdata. On SlideSpeak every page carries 2–4 JSON-LD blocks
    // (Organization, BreadcrumbList, Article) and the check still fired on all 25; the grader graded
    // all 25 wrong. ManyRequests had none then, and its rows were graded right; since it left Webflow
    // 368 of its 397 flagged pages carry one (2026-11-03).
    const facts = storedMarkupFacts(analysis);
    return facts && facts.jsonLdBlocks > 0
      ? `The page already has structured data: ${facts.jsonLdBlocks} JSON-LD block${facts.jsonLdBlocks === 1 ? '' : 's'}, which the crawler's check does not count.`
      : null;
  }
  if (issue.code === 'no_image_alt') {
    // SlideSpeak: the only images without alt text are tracking pixels and images marked
    // decorative on every queue page but one, and the graders graded those rows wrong (sprint 2 S0,
    // sprint 6 S4). Every ManyRequests page flagged has content images with empty alt beside its
    // navigation icons, and those rows were graded right.
    const facts = storedMarkupFacts(analysis);
    return facts && facts.images > 0 && facts.imagesNeedingAlt === 0
      ? 'Every image without alt text is hidden, a tracking pixel, or marked decorative (an empty alt on an icon, a vector graphic or a background), which is correct as it is.'
      : null;
  }
  if (issue.code === 'https_to_http_links') {
    // Read from the same crawl as the check, so this only sets aside a check the crawl's own copy
    // contradicts. On ManyRequests (2026-11-03) the copy agreed on all 44 pages flagged: article
    // links such as `http://make.com`, which the site changed to https after the crawl. Those clear
    // at the next crawl, not here.
    return storedHtmlHasHttpLink(analysis) === false
      ? 'The page links nowhere over plain http: every link and resource in it is https, so there is nothing to change.'
      : null;
  }
  if (CLOSING_TAG_MISMATCH_CHECK.test(issue.code)) {
    return "The crawler's HTML check misreads valid markup here, such as mixed-case SVG tags. Browsers parse the page without a mismatch, so there is nothing to fix.";
  }
  if (NODE_CHILDREN_CHECK.test(issue.code)) {
    return 'The crawler flags the first element with more than 60 children, which is the document head or body, not anything a writer edits. A long flat list of paragraphs costs nothing.';
  }
  return null;
}

/**
 * Whether the stored HTML shows a check to be this page's own, whatever the domain rollup says.
 *
 * The rollup calls a check site-wide by how many pages carry it, not by where on the page it sits.
 * - `no_image_alt`: when none of the images that need alt text is in the site's navigation, header,
 *   footer or sidebar, they are the page's own (article screenshots, a hero, a preview). On the
 *   SlideSpeak post with 19 tutorial screenshots the item said "the site template", graded
 *   unfinished: "real problem, wrong place".
 * - `low_content_rate`: once the template's inline styles are left out, a page still under the bar
 *   (by more than the margin) is short on copy itself. Its siblings on the same template run
 *   18–39% on the same measure on ManyRequests' queue, where the rollup calls the check site-wide.
 */
export function storedHtmlScopesCheckToPage(code: string, analysis: unknown): boolean {
  if (code === 'no_image_alt') {
    const facts = storedMarkupFacts(analysis);
    return !!facts && facts.imagesNeedingAlt > 0 && facts.imagesNeedingAltInChrome === 0;
  }
  if (code === 'low_content_rate') {
    const rate = textRateWithoutStyles(analysis);
    return rate != null && rate < LOW_CONTENT_RATE_BAR - LOW_CONTENT_RATE_MARGIN;
  }
  return false;
}

/**
 * A page's technical findings, split into what the review surface shows and what the stored crawl
 * contradicts.
 *
 * Order is kept: `shown` is `views` minus the withheld ones, so the ticks, the payload and the count
 * chip that read it list the checks in the order URL Detail does.
 */
export function reviewTechnicalChecks(views: readonly OnpageIssueView[], analysis: unknown): ReviewTechnicalChecks {
  const shown: OnpageIssueView[] = [];
  const withheld: WithheldTechnicalCheck[] = [];
  for (const issue of views) {
    const reason = withheldReason(issue, analysis);
    if (reason) withheld.push({ code: issue.code, title: issue.title, reason });
    else shown.push(issue);
  }
  return { shown, withheld };
}
